import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { UserModel } from '../../../../../models/user-permissions.model';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { Observable, zip } from 'rxjs';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../permission-controller/root';
import { UserPermActions } from '../../../user-permissions-controller/actions';
import { UserPermSelectors } from '../../../user-permissions-controller/selectors';
import { UserPermStates } from '../../../user-permissions-controller/states';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  deactivateList: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-user-permission-view',
  templateUrl: './user-permission-view.component.html',
  styleUrls: ['./user-permission-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class UserPermissionViewComponent extends ViewColumnComponent {


  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 1;
  private localState: LocalState;

  // info. about previous local states
  prevIndex: number;
  protected prevLocalState: any;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.selectedIndex));

  // api visa
  apiVisa = AppConfig.apiVisa;

  toggleColumn$: Observable<boolean>;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  public form: FormGroup;

  currentIndexTab: number;

  formDisabled: boolean = false;

  currentUser: UserModel;
  selectedUser: UserModel;
  readonly selectedUser$ = this.store.select(UserPermSelectors.selectSelectedUser)

  //translation variable
  title: string;
  details_text: string;
  email_text: string;
  mobile_text: string;
  rank_text: string;
  role_text: string;
  permission_set_listing_text: string;
  permission_definition_text: string;
  back_button: string;

  constructor(
    private fb: FormBuilder,
    private store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    protected readonly componentStore: ComponentStore<LocalState>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.view_user_permission;
        this.details_text = res.details;
        this.email_text = res.email;
        this.mobile_text = res.mobile;
        this.rank_text = res.rank;
        this.role_text = res.role;
        this.permission_set_listing_text = res.permission_set_listing;
        this.permission_definition_text = res.permission_definition;
        this.back_button = res.back_button;
      });
    } else {
      // Fallback if translation service is unavailable
      this.title = 'View User Permission';
      this.details_text = 'Details';
      this.email_text = 'Email';
      this.mobile_text = 'Mobile';
      this.rank_text = 'Rank';
      this.role_text = 'Role';
      this.permission_set_listing_text = 'Permission Set Listing';
      this.permission_definition_text = 'Permission Definition';
      this.back_button = 'Back';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit() {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
    });

    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
        email: [''],
        mobile: [''],
        rank: ['', Validators.compose([Validators.required])]
    })

    this.subSink.sink = this.selectedUser$
    .subscribe(selectedUser => {
      console.log("DEBUG>>>>>>>>>", selectedUser);
      // set form values
      this.form.patchValue({
        email: selectedUser.main_email,
        mobile: selectedUser.main_phone,
        rank: selectedUser.userModel.bl_applet_login_subject_link.rank,
      });
    })

  }

  goToRoleDetailAndPermSetList(roleEntity: any) {
    // dispatch ngrx action
    this.store.dispatch(UserPermActions.selectRole({role: roleEntity}));
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateList: false,
          deactivateReturn: true,
        });
      // trigger the next view column
      this.viewColFacade.onNextAndReset(this.index, 2);
    }
  }

  goToPermissionSetDetailAndPermSetLine(permSetEntity) {
    console.log("PermSetDetailAndPermSetLine");

    // dispatch ngrx action
    this.store.dispatch(UserPermActions.selectPermSet({permSet: permSetEntity}));

    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateAdd: false,
          deactivateList: false,
          deactivateReturn: true,
        });
      // trigger the next view column
      this.viewColFacade.onNextAndReset(this.index, 10);
    }
  }

  goToCreatePermSet() {
    console.log("CreatePermSet");

    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateAdd: true,
        deactivateList: false,
        deactivateReturn: true,
      });
    // trigger the add permission set component to be shown
    this.viewColFacade.onNextAndReset(this.index, 9);
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance<LocalState>(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
